/*
 * @Copyright 2026. Institute for Future Intelligence, Inc.
 *
 * Internal preset matcher — tier 0 of the crystal lookup chain (see
 * crystalLookup.ts for the COD tier and the chain itself). Pure: no network,
 * no Firebase, no LLM — kept in its own module so the standalone verifier
 * (crystalLookup.test.ts) can import it without the Firebase client, which
 * reads Vite env at import time.
 */

import { ALL_PRESETS } from './presets.ts';
import { CrystalState } from '../types.ts';
import { chemicalFormula } from './Crystal.ts';
import { isKnownElementSymbol } from './elementSymbol.ts';
import Element from '../lib/chem/Element';

export interface PublicCrystalResult {
  // Deep-copied CrystalState so the caller can mutate without touching
  // the preset definition.
  crystal: CrystalState;
  source: 'internal' | 'cod';
  // Human-readable source label for the gallery card's `source` field —
  // e.g. "Internal database" or "COD 1234567".
  sourceLabel: string;
  // Best name to use for the gallery entry; usually the preset's stored
  // English name (which round-trips through getCrystalDisplayName for
  // localization at render time).
  fallbackName: string;
}

// Lowercase letters / digits only, collapse whitespace. Used to compare
// natural-language fragments against preset names without worrying about
// punctuation, case, or Unicode subscript variants.
const normalizeText = (s: string): string => s.toLowerCase().replace(/[^a-z0-9]/g, '');

// Strip Unicode subscripts and brackets from formulas so user input
// ("NaCl", "Si") matches preset formulas that may include subscripts
// ("CaF₂") or vice versa.
const SUB_TO_DIGIT: Record<string, string> = {
  '₀': '0',
  '₁': '1',
  '₂': '2',
  '₃': '3',
  '₄': '4',
  '₅': '5',
  '₆': '6',
  '₇': '7',
  '₈': '8',
  '₉': '9',
};
const normalizeFormula = (s: string): string => {
  let out = '';
  for (const ch of s) out += SUB_TO_DIGIT[ch] ?? ch;
  // Strip whitespace and any non-letter/digit, keep case to preserve
  // element symbols (Na, Cl, K, etc.).
  return out.replace(/[^A-Za-z0-9]/g, '');
};

// Filler words / phrasing the user might wrap around a chemical name.
// Stripped before name-matching so "show me silicon" still hits the
// Silicon preset. Order doesn't matter — applied with a global regex.
const FILLER_PHRASES = [
  'show me',
  'show',
  'load',
  'open',
  'display',
  'render',
  'draw',
  'generate',
  'create',
  'make',
  'build',
  'a',
  'an',
  'the',
  'crystal of',
  'crystal',
  'structure of',
  'structure',
  'lattice of',
  'lattice',
  'unit cell of',
  'unit cell',
  'please',
  "i'd like",
  'i want',
  'can you',
  'give me',
];

// Every element's English name, normalized ("lead", "sulfur"), to its symbol,
// plus the British spellings the table does not carry. A query that is just one
// of these asks for the ELEMENT: its containment hits are limited to presets made
// of that element alone ("phosphorus" → White Phosphorus), never a compound whose
// name merely contains it — "lead" reached Lead Sulfide, "sulfur" Sulfur
// Hexafluoride, "carbon" Carbon Monoxide. With no such preset the query falls
// through to the formula pass and then COD / the AI.
const ELEMENT_NAMES: ReadonlyMap<string, string> = new Map([
  ...(Element.ByAtomicNumber as unknown as ({ number: number; name: string; fullName: string } | null)[])
    .filter(
      (e): e is { number: number; name: string; fullName: string } => !!e && e.number > 0 && !!e.fullName && !!e.name,
    )
    .map((e) => [normalizeText(e.fullName), e.name[0] + e.name.slice(1).toLowerCase()] as [string, string]),
  ['aluminium', 'Al'],
  ['caesium', 'Cs'],
  ['sulphur', 'S'],
]);

// Plain words that mean one specific preset whose name does not say so. Checked
// after the exact-name pass, before containment. A target missing from the
// catalogue is skipped (the query then falls through as usual).
const NAME_ALIASES: Record<string, string> = {
  tin: 'Gray Tin', // the stable form below 13 °C, and the only elemental tin preset
  graytin: 'Gray Tin',
  greytin: 'Gray Tin',
  alphatin: 'Gray Tin',
  carbon: 'Diamond', // the conventional "carbon crystal"; Graphite is the other preset
  ice: 'Cubic Ice', // water ice, not Dry Ice (solid CO₂)
  waterice: 'Cubic Ice',
  frozenwater: 'Cubic Ice',
};

// Is `f` (normalizeFormula'd) a formula written in proper element case —
// every symbol a capital plus an optional lowercase letter, and a real
// element? "CO", "NaCl", "CO2" are; "nacl" (all lowercase) and "NACL" (A, L
// are no elements) are not. Only such a query can tell CO from Co.
const isCasedFormula = (f: string): boolean =>
  /^([A-Z][a-z]?\d*)+$/.test(f) && (f.match(/[A-Z][a-z]?/g) ?? []).every((el) => isKnownElementSymbol(el));

const stripFiller = (prompt: string): string => {
  let p = ' ' + prompt.toLowerCase() + ' ';
  for (const phrase of FILLER_PHRASES) {
    p = p.split(' ' + phrase + ' ').join(' ');
  }
  return p.trim();
};

// Deep-copy a preset CrystalState so the caller's edits don't mutate
// the in-memory preset definition. Matches what ImportCrystalModal /
// projectGallery.importCrystalsByNames do on the fast paths.
const cloneCrystal = (preset: CrystalState): CrystalState => ({
  ...preset,
  lattice: { ...preset.lattice },
  basis: preset.basis.map((b) => ({ ...b })),
});

// Try to match `prompt` against the internal preset catalogue. Returns
// a deep-copied CrystalState on hit, or null on miss. Pure function —
// no network, no LLM.
// Other names for the curated zeolites: IZA framework codes and the usual
// trade / material names. Keys and values are normalizeText()'d; the value is
// the preset name. Without these, "silicalite-1", "MFI" or "LTA zeolite"
// fell through to COD and came back as cation-exchanged aluminosilicates.
const ZEOLITE_ALIASES: Record<string, string> = {
  cha: 'chabazite',
  chazeolite: 'chabazite',
  ssz13: 'chabazite',
  mfi: 'silicalite',
  mfizeolite: 'silicalite',
  silicalite1: 'silicalite',
  zsm5: 'silicalite',
  lta: 'zeolitea',
  ltazeolite: 'zeolitea',
  itq29: 'zeolitea',
  zeolite4a: 'zeolitea',
  linde4a: 'zeolitea',
  lindetypea: 'zeolitea',
  fau: 'faujasite',
  fauzeolite: 'faujasite',
  zeolitey: 'faujasite',
  zeolitex: 'faujasite',
  mor: 'mordenite',
  morzeolite: 'mordenite',
  sod: 'sodalite',
  sodzeolite: 'sodalite',
};
// Qualifiers that name the composition the zeolite presets already are.
const ALL_SILICA = /\b(all|pure|purely)[\s-]*silica\b|\bsiliceous\b|\bhigh[\s-]*silica\b/gi;

export const lookupInternalCrystal = (prompt: string): PublicCrystalResult | null => {
  const raw = prompt.trim();
  if (!raw) return null;
  const collapsed = raw.replace(ALL_SILICA, ' ').replace(/\s+/g, ' ');
  const stripped = stripFiller(collapsed);
  const plain = normalizeText(stripped);
  const normName = ZEOLITE_ALIASES[plain] ?? plain;
  const normFormula = normalizeFormula(stripped);
  // The same text in the user's own letter case (stripFiller lowercases), for
  // the formula pass: lowercasing turned "CO" into "co", which matched Cobalt.
  const at = collapsed.toLowerCase().indexOf(stripped);
  const casedFormula = normalizeFormula(at >= 0 ? collapsed.slice(at, at + stripped.length) : stripped);

  const hit = (preset: CrystalState): PublicCrystalResult => ({
    crystal: cloneCrystal(preset),
    source: 'internal',
    sourceLabel: 'Internal database',
    fallbackName: preset.name ?? '',
  });

  // 1a. EXACT name match, over the whole catalogue, before any containment
  //     match is considered. The two used to share one pass in list order,
  //     and the list opens with the salts: "sodium" reached Sodium Chloride
  //     (which contains it) before the Sodium preset further down — likewise
  //     lithium, zinc, nickel, magnesium, aluminum, beryllium — so a bare
  //     element name loaded a compound under a confident "Internal database"
  //     label.
  if (normName.length >= 3) {
    for (const preset of ALL_PRESETS) {
      if (preset.name && normalizeText(preset.name) === normName) return hit(preset);
    }
    // 1a'. A word that means one preset whose name does not contain it.
    const aliasTarget = NAME_ALIASES[normName];
    if (aliasTarget) {
      const preset = ALL_PRESETS.find((p) => p.name === aliasTarget);
      if (preset) return hit(preset);
    }
  }
  // 1b. Containment. For a bare element name, only presets made of that
  //     element alone qualify (see ELEMENT_NAMES).
  if (normName.length >= 3) {
    const elementSymbol = ELEMENT_NAMES.get(normName);
    // Containment, on WHOLE WORDS of the preset name: the query must equal
    //     a contiguous run of the name's words. Hits "sodium chloride" inside
    //     "Sodium Chloride (rock salt)" and "chabazite" inside a longer zeolite
    //     name, but "tin" no longer finds Pla-tin-um. Requires ≥ 3 chars to
    //     avoid spurious 2-letter hits.
    for (const preset of ALL_PRESETS) {
      if (!preset.name) continue;
      if (elementSymbol && !preset.basis.every((b) => b.element === elementSymbol)) continue;
      const words = preset.name
        .toLowerCase()
        .split(/[^a-z0-9]+/)
        .filter(Boolean);
      for (let i = 0; i < words.length; i++) {
        let run = '';
        for (let j = i; j < words.length && run.length < normName.length; j++) {
          run += words[j];
          if (run === normName) return hit(preset);
        }
      }
    }
  }

  // 2. Formula match. Compute the preset's empirical formula from its
  //    basis (same path as the gallery card label) and compare in
  //    normalized form. Hits "NaCl" → preset whose formula reduces to
  //    NaCl, "BaTiO3" / "BaTiO₃" → Barium Titanate.
  //    Compare CASE-INSENSITIVELY: stripFiller lowercases the user's text, so
  //    the query side is already lowercase ("cscl"), while the preset's
  //    canonical formula keeps element-symbol case ("CsCl"). Without lowering
  //    both sides every formula query missed (e.g. "CsCl" / "NaCl" typed as a
  //    formula), silently falling through to AI generation.
  //    …unless the user wrote the formula in proper element case: then case
  //    is meaning, not noise. "CO" is carbon monoxide, "Co" cobalt, "CS" not
  //    caesium — compared case-insensitively, "CO" loaded the Cobalt preset
  //    (listed before Carbon Monoxide). An all-lowercase or all-caps query
  //    ("nacl", "NACL") has no usable case and still matches either way.
  if (normFormula.length >= 2) {
    const cased = isCasedFormula(casedFormula);
    const queryFormula = cased ? casedFormula : normFormula.toLowerCase();
    for (const preset of ALL_PRESETS) {
      const formula = chemicalFormula(preset.basis);
      if (!formula) continue;
      const presetFormula = normalizeFormula(formula);
      if ((cased ? presetFormula : presetFormula.toLowerCase()) === queryFormula) {
        return {
          crystal: cloneCrystal(preset),
          source: 'internal',
          sourceLabel: 'Internal database',
          fallbackName: preset.name ?? formula,
        };
      }
    }
  }

  return null;
};
